import React, { useState, useCallback, memo } from "react";
import { Form, Button, Alert, Spinner } from "react-bootstrap";

const FormFields = memo(({ fields, formData, onInputChange }) => {
  const handleChange = useCallback((fieldId, value) => {
    onInputChange(fieldId, value);
  }, [onInputChange]);

  return (
    <>
      {fields.map((field) => (
        <Form.Group className="mb-4" key={field.id}>
          <Form.Label className="text-white mb-2" style={{ fontSize: "0.9em", fontWeight: "500" }}>
            {field.label}
          </Form.Label>
          {field.type === "textarea" ? (
            <Form.Control
              as="textarea"
              rows={3}
              className="dark-input"
              placeholder={`Enter ${field.label.toLowerCase()}...`}
              value={formData[field.id] || ""}
              onChange={(e) => handleChange(field.id, e.target.value)}
              autoComplete="off"
              style={{
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                color: "#ffffff",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                borderRadius: "8px",
                padding: "12px",
                transition: "all 0.3s ease-in-out"
              }}
            />
          ) : (
            <Form.Control
              type={field.type}
              className="dark-input"
              placeholder={`Enter ${field.label.toLowerCase()}...`}
              value={formData[field.id] || ""}
              onChange={(e) => handleChange(field.id, e.target.value)}
              autoComplete="off"
              style={{
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                color: "#ffffff",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                borderRadius: "8px",
                padding: "12px",
                transition: "all 0.3s ease-in-out"
              }}
            />
          )}
        </Form.Group>
      ))}
    </>
  );
});

const ContractForm = memo(({ fields, initialFormData = {}, onInputChange: parentOnInputChange, onBack, isLoading }) => {
  const [localFormData, setLocalFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const handleInputChange = useCallback((fieldId, value) => {
    setLocalFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || isLoading) return;
    
    try {
      setError(null);
      setIsSubmitting(true);
      await parentOnInputChange(localFormData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(error.message || 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  }, [localFormData, parentOnInputChange, isSubmitting, isLoading]);

  const isButtonDisabled = isSubmitting || isLoading;
  
  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      {error && (
        <Alert 
          variant="danger" 
          className="mb-4"
          style={{
            background: "rgba(220, 53, 69, 0.1)",
            border: "1px solid rgba(220, 53, 69, 0.2)",
            color: "#ffffff",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            borderRadius: "8px"
          }}
        >
          {error}
        </Alert>
      )}
      <FormFields 
        fields={fields}
        formData={localFormData}
        onInputChange={handleInputChange}
      />
      <div className="mt-4 d-flex justify-content-between">
        <Button 
          style={{
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            color: "#ffffff",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            padding: "10px 20px",
            transition: "all 0.3s ease-in-out",
            borderRadius: "8px",
            fontSize: "0.9em",
            fontWeight: "500"
          }}
          type="button" 
          onClick={onBack}
          disabled={isButtonDisabled}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.border = "1px solid rgba(255, 255, 255, 0.12)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
            e.currentTarget.style.border = "1px solid rgba(255, 255, 255, 0.08)";
          }}
        >
          Back
        </Button>
        <Button
          style={{
            background: "rgba(103, 58, 183, 0.3)",
            border: "1px solid rgba(149, 117, 205, 0.25)",
            color: "#ffffff",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            padding: "10px 24px",
            transition: "all 0.3s ease-in-out",
            borderRadius: "8px",
            fontSize: "0.9em",
            fontWeight: "500"
          }}
          type="button" 
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(103, 58, 183, 0.4)";
            e.currentTarget.style.border = "1px solid rgba(149, 117, 205, 0.35)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(103, 58, 183, 0.3)";
            e.currentTarget.style.border = "1px solid rgba(149, 117, 205, 0.25)";
          }}
        >
          {(isSubmitting || isLoading) ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
                style={{ borderWidth: "2px" }}
              />
              Generating...
            </>
          ) : (
            'Generate Contract'
          )}
        </Button>
      </div>
    </Form>
  );
});

export default ContractForm; 